/* components/patterns/PlanPeriodModal.jsx — выбор периода оплаты тарифа-семейства.
   Семейство — месячный базовый тариф (slug=base) + длинные варианты (slug=base_<N>m).
   Месячная цена базы — точка отсчёта: длинный вариант показывает цену за месяц,
   итог за срок, сумму экономии и подсвеченную скидку (−5% / −10%) относительно
   base.price × месяцев. Скидку считаем из данных (не хардкодим), округляя до целых %.
   onSelect(slug, price) — то же действие, что кнопка тарифа в гриде (апгрейд → checkout,
   даунгрейд → отложенная смена); решение up/down принимает вызывающий по цене. */

import { quotaText, fileSpecs, isBusinessPlan } from './PlanCard.jsx';
import { Pill } from '../ui/Badge.jsx';
import { Button } from '../ui/Button.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Modal } from '../ui/Modal.jsx';
import { fmt } from '../../lib/format.js';

  const monthsLabel = (m) => m + ' ' + fmt.plural(m, 'месяц', 'месяца', 'месяцев');

  // family: { base: plan, variants: [{ plan, months }] } → [{ plan, months, full, perMonth, savings, discountPct }]
  function buildOptions(family) {
    if (!family || !family.base) return [];
    const baseMonthly = family.base.price_rub || 0;
    const baseOpt = { plan: family.base, months: 1, full: baseMonthly, perMonth: baseMonthly, savings: 0, discountPct: 0 };
    const variants = (family.variants || [])
      .slice()
      .sort((a, b) => a.months - b.months)
      .map(({ plan, months }) => {
        const price = plan.price_rub || 0;
        const full = baseMonthly * months;
        return {
          plan, months, full,
          perMonth: months ? Math.round(price / months) : price,
          savings: Math.max(0, full - price),
          discountPct: full > 0 ? Math.round((1 - price / full) * 100) : 0,
        };
      });
    return [baseOpt, ...variants];
  }

  function PlanPeriodModal({ open, family, currentSlug, pendingSlug, busy = false, onSelect, onClose }) {
    const base = family && family.base;
    const specs = base ? quotaText(base) : null;
    const files = base && isBusinessPlan(base) ? fileSpecs(base) : null;
    const options = buildOptions(family);
    // Лучшая скидка — ОДНА: при равном проценте берём самый длинный период (опции
    // отсортированы по возрастанию месяцев, поэтому подходит последний с max %).
    const bestPct = options.reduce((mx, o) => Math.max(mx, o.discountPct), 0);
    const bestSlug = bestPct > 0
      ? options.filter((o) => o.discountPct === bestPct).slice(-1)[0].plan.slug
      : null;

    return (
      <Modal open={!!open && !!base} onClose={onClose} size="md" labelledBy="period-modal-title"
        title={base ? ('Тариф ' + base.name) : 'Выбор периода'}>
        <div className="stack stack-12">
          <p className="t-body-sm text-mute">Один тариф — разный срок оплаты. Чем длиннее период, тем ниже цена за месяц.</p>
          {specs && (
            <div className="period-quota">
              <span className="period-quota__item"><Icon name="chat" size={14} /><span>{specs.requests} запросов в месяц</span></span>
              <span className="period-quota__item"><Icon name="microphone" size={14} /><span>{specs.audio} аудио в месяц</span></span>
              {files && <>
                <span className="period-quota__item"><Icon name="file" size={14} /><span>{files.documents} документов · {files.storage}</span></span>
                <span className="period-quota__item"><Icon name="upload" size={14} /><span>файл до {files.fileSize}</span></span>
              </>}
            </div>
          )}
          <ul className="period-opts">
            {options.map((o) => {
              const isCurrent = o.plan.slug === currentSlug;
              const isPending = !isCurrent && o.plan.slug === pendingSlug;
              const isBest = o.plan.slug === bestSlug;
              const hasDiscount = o.discountPct > 0;   // единый флаг: и пилюля, и «экономия»
              return (
                <li key={o.plan.slug} className={'period-opt' + (isBest ? ' is-best' : '') + (isCurrent ? ' is-current' : '')}>
                  <div className="period-opt__main">
                    <div className="period-opt__head">
                      <span className="period-opt__term t-label">{monthsLabel(o.months)}</span>
                      {hasDiscount && <Pill tone="ok" glyph={false} size="sm">−{o.discountPct}%</Pill>}
                      {isBest && <Pill tone="accent" glyph={false} size="sm">выгоднее всего</Pill>}
                    </div>
                    <div className="period-opt__price">
                      <span className="period-opt__permonth num">{fmt.money(o.perMonth)}<span className="text-mute"> / мес</span></span>
                      <span className="period-opt__total t-caption text-mute">
                        {o.months === 1
                          ? 'оплата помесячно'
                          : (fmt.money(o.plan.price_rub) + ' за ' + monthsLabel(o.months))}
                        {hasDiscount && (' · экономия ' + fmt.money(o.savings))}
                      </span>
                    </div>
                  </div>
                  <div className="period-opt__action">
                    {isCurrent
                      ? <Button variant="secondary" size="md" disabled iconLeft="check">Текущий</Button>
                      : isPending
                        ? <Button variant="secondary" size="md" disabled iconLeft="clock">Запланировано</Button>
                        : <Button variant={isBest ? 'primary' : 'secondary'} size="md" loading={busy} disabled={busy}
                            onClick={() => onSelect && onSelect(o.plan.slug, o.plan.price_rub)}>Оплатить</Button>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </Modal>
    );
  }

export { PlanPeriodModal };
export { buildOptions };
